/**
 * DOCUMENTATION:
 * Component: SystemDesignProject
 * Purpose: A capstone architectural walkthrough of a Scalable URL Shortener.
 * * * Note for Junior Developers:
 * - Functional Requirements: What the system DOES (Shorten, Redirect).
 * - Non-Functional Requirements: How the system BEHAVES (High Availability, Low Latency).
 * - Estimation: Architects always estimate traffic. If we have 100M new URLs per month,
 * how many writes per second is that? (100M / 30 days / 24 hrs / 3600 sec ≈ 40 writes/sec).
 */

import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack, Divider
} from "@mui/material";

/* Icons */
import ArchitectureIcon from '@mui/icons-material/Architecture';
import AssignmentIcon from '@mui/icons-material/Assignment';
import StorageIcon from '@mui/icons-material/Storage';
import LanIcon from '@mui/icons-material/Lan';

const bgImage = "https://images.unsplash.com/photo-1451187580459-43490279c0fa";

/* Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(10, 5, 25, 0.9), rgba(10, 5, 25, 0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(20, 15, 45, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    color: "white",
}));

const TechBadge = styled(Box)(({ theme }) => ({
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: "4px",
    backgroundColor: "rgba(0, 200, 255, 0.1)",
    border: "1px solid #00c8ff",
    color: "#00c8ff",
    fontSize: "0.75rem",
    fontWeight: 700,
    marginRight: "8px",
    marginBottom: "8px"
}));

export default function SystemDesignProject() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "#00c8ff" }}>
                    PROJECT: URL SHORTENER
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 1. Requirements Clearing */}
                <Grid item xs={12} md={6}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                            <AssignmentIcon sx={{ color: "#00c8ff" }} />
                            <Typography variant="h6" fontWeight={800}>Requirements</Typography>
                        </Stack>
                        <Typography variant="subtitle2" color="#00c8ff" gutterBottom>Functional:</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                            • Shorten long URLs into unique 7-character aliases.<br />
                            • Redirect users to original URL upon clicking short link.<br />
                            • Custom short-links (e.g., bit.ly/my-awesome-link).
                        </Typography>
                        <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.1)" }} />
                        <Typography variant="subtitle2" color="#4ade80" gutterBottom>Non-Functional:</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            • <strong>Availability:</strong> 99.99% uptime.<br />
                            • <strong>Latency:</strong> Redirection must be sub-100ms.<br />
                            • <strong>Scalability:</strong> Support millions of requests per day.
                        </Typography>
                    </SectionCard>
                </Grid>

                {/* 2. Data Model */}
                <Grid item xs={12} md={6}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                            <StorageIcon sx={{ color: "#00c8ff" }} />
                            <Typography variant="h6" fontWeight={800}>The Data Model</Typography>
                        </Stack>
                        <Box sx={{ p: 2, bgcolor: "rgba(0,0,0,0.2)", borderRadius: "8px", fontFamily: "monospace" }}>
                            <Typography variant="caption" color="gray">// URL Table</Typography>
                            <Typography variant="body2">{'{'}</Typography>
                            <Typography variant="body2" sx={{ ml: 2 }}>shortHash: "aB12xK9", // PK</Typography>
                            <Typography variant="body2" sx={{ ml: 2 }}>originalUrl: "https://google.com/search?q=...",</Typography>
                            <Typography variant="body2" sx={{ ml: 2 }}>userId: "user_99",</Typography>
                            <Typography variant="body2" sx={{ ml: 2 }}>createdAt: 1713348000</Typography>
                            <Typography variant="body2">{'}'}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 2, opacity: 0.7 }}>
                            <strong>Storage Choice:</strong> A NoSQL Key-Value store (like DynamoDB or Cassandra) is ideal here because we only ever fetch by the <code>shortHash</code>.
                        </Typography>
                    </SectionCard>
                </Grid>

                {/* 3. The Architecture Diagram Walkthrough */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                            <LanIcon sx={{ color: "#00c8ff" }} />
                            <Typography variant="h5" fontWeight={800}>High-Level Architecture</Typography>
                        </Stack>



                        <Grid container spacing={3} mt={2}>
                            <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" color="#00c8ff" gutterBottom>1. Read Layer (The Edge)</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    Since redirects are frequent, we use <strong>Redis</strong> for caching. 80% of traffic hits the cache, 20% hits the DB.
                                </Typography>
                                <Box mt={1}>
                                    <TechBadge>Redis</TechBadge><TechBadge>Cloudflare</TechBadge>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" color="#00c8ff" gutterBottom>2. Write Layer (Shortening)</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    We use <strong>Base62 encoding</strong> on a distributed ID generator (Snowflake ID) to ensure no collisions.
                                </Typography>
                                <Box mt={1}>
                                    <TechBadge>Node.js</TechBadge><TechBadge>Base62</TechBadge>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" color="#00c8ff" gutterBottom>3. Analytics (Async)</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    We don't block the redirect for analytics. We drop an event into <strong>Kafka</strong> to be processed later.
                                </Typography>
                                <Box mt={1}>
                                    <TechBadge>Kafka</TechBadge><TechBadge>ClickHouse</TechBadge>
                                </Box>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/system-design-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#00c8ff", color: "#0a0519", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
